/**
 * WholeDayOptimizeModal (OPTIMIZE-DAY) — review + edit + apply a board-wide
 * optimize. The proposed plan is laid out as a board of per-tech lanes; the
 * dispatcher can:
 *   - DRAG a call within a lane to reorder it, or onto another tech's lane to
 *     reassign it — anchored calls (PART/TECH/Install/Requested/locked) can't
 *     change tech, and a movable call only lands on a QUALIFIED tech (skills +
 *     not an excluded tech),
 *   - HOVER any call to see what it's about (so sliding it is informed),
 *   - revert a moved call back to its original tech,
 * then APPLY — the parent writes the kept reassignments (ESC override / Pegasus)
 * + each tech's run order, with Undo. Live miles update as you edit.
 */
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Sparkles,
  Lock,
  TrendingDown,
  Route,
  GripVertical,
  ArrowRight,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/lib/cn";
import { routeMiles } from "@/lib/route";
import type { OptiStop, WholeDayPlan } from "@/lib/boardOptimize";
import { MovePreview, type PendingMove } from "./MovePreview";
import type { RecoTech } from "@/lib/recommendations";
import type { Dispatch } from "@/lib/types";

export interface WholeDayCommit {
  reassignments: { id: string; from: string; to: string }[];
  routePlans: { tech: string; order: string[] }[];
  movedCount: number;
  savedMiles: number;
}

export function WholeDayOptimizeModal({
  plan,
  stops,
  techName,
  recoTechs,
  rows,
  onApply,
  applying = false,
  onClose,
}: {
  plan: WholeDayPlan;
  stops: OptiStop[];
  techName: (empno: string) => string;
  recoTechs: RecoTech[];
  rows: Dispatch[];
  onApply: (commit: WholeDayCommit) => void;
  applying?: boolean;
  onClose: () => void;
}) {
  const byId = useMemo(() => new Map(stops.map((s) => [s.id, s] as const)), [stops]);
  const [preview, setPreview] = useState<PendingMove | null>(null);
  const [hover, setHover] = useState<{ stop: OptiStop; x: number; y: number } | null>(
    null,
  );
  const showHover = (stop: OptiStop, e: React.MouseEvent) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover({ stop, x: r.right + 8, y: r.top });
  };

  // The editable board: per-tech ordered stop ids, seeded from the optimizer's
  // proposal. Drag mutates it.
  const [board, setBoard] = useState<Record<string, string[]>>(() => {
    const b: Record<string, string[]> = {};
    for (const [tech, ids] of Object.entries(plan.perTech)) b[tech] = [...ids];
    return b;
  });
  const assignOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const [tech, ids] of Object.entries(board))
      for (const id of ids) m.set(id, tech);
    return m;
  }, [board]);
  const techSkills = useMemo(() => {
    const m = new Map<string, Set<number>>();
    for (const t of recoTechs) m.set(t.empno, t.skills);
    return m;
  }, [recoTechs]);
  // Can tech `empno` take movable stop `s`? (skills + not an excluded tech)
  const qualifies = (s: OptiStop, empno: string) => {
    if (empno === s.empno) return true;
    if ((s.excludedEmpnos ?? []).includes(empno)) return false;
    const sk = techSkills.get(empno);
    if (!sk) return false;
    return (s.requiredSkills ?? []).every((d) => sk.has(d));
  };
  // Lanes = every tech in the plan + roster, so you can drop onto an empty tech.
  const laneTechs = useMemo(() => {
    const set = new Set<string>(Object.keys(board));
    for (const t of recoTechs) set.add(t.empno);
    return [...set].sort((a, b) => techName(a).localeCompare(techName(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, recoTechs]);

  // --- Drag (pointer-capture + elementFromPoint; the list reflows under the
  //     pointer — the dragged card is pointer-events-none so hit-tests pass it). ---
  const [dragId, setDragId] = useState<string | null>(null);
  const dragRef = useRef<string | null>(null);
  const [reject, setReject] = useState(false); // brief flash when a drop is invalid
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Pure: drop `id` into `toTech` before `overId` (or at the end). Caller validates.
  const moveInBoard = (
    prev: Record<string, string[]>,
    id: string,
    toTech: string,
    overId: string | null,
  ): Record<string, string[]> => {
    const next: Record<string, string[]> = {};
    for (const [t, ids] of Object.entries(prev)) next[t] = ids.filter((x) => x !== id);
    const lane = next[toTech] ? [...next[toTech]] : [];
    const idx = overId ? lane.indexOf(overId) : -1;
    lane.splice(idx < 0 ? lane.length : idx, 0, id);
    next[toTech] = lane;
    return next;
  };

  const startDrag = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    dragRef.current = id;
    setDragId(id);
    setReject(false);
    try {
      containerRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // capture unsupported — move/up still work
    }
  };
  const onDragMove = (e: React.PointerEvent) => {
    const id = dragRef.current;
    if (!id) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const toTech = el?.closest("[data-lane]")?.getAttribute("data-lane");
    if (!toTech) return;
    const overId = el?.closest("[data-stop]")?.getAttribute("data-stop") ?? null;
    if (overId === id) return;
    // Cross-lane only for a movable call landing on a qualified tech.
    const s = byId.get(id);
    const fromTech = assignOf.get(id);
    if (toTech !== fromTech && s && (!s.movable || !qualifies(s, toTech))) {
      setReject(true);
      return;
    }
    setReject(false);
    setBoard((prev) => moveInBoard(prev, id, toTech, overId));
  };
  const endDrag = (e: React.PointerEvent) => {
    dragRef.current = null;
    setDragId(null);
    setTimeout(() => setReject(false), 250);
    try {
      if (containerRef.current?.hasPointerCapture(e.pointerId))
        containerRef.current.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const revertToOrigin = (id: string) => {
    const s = byId.get(id);
    if (!s) return;
    setBoard((prev) => moveInBoard(prev, id, s.empno, null));
  };

  const movableCount = stops.filter((s) => s.movable).length;
  const locked = stops.filter((s) => !s.movable);
  const lockedByReason = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of locked)
      m.set(s.lockReason ?? "Locked", (m.get(s.lockReason ?? "Locked") ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [locked]);

  // Live committed plan from the current board.
  const commit = useMemo<WholeDayCommit>(() => {
    const routePlans: { tech: string; order: string[] }[] = [];
    let milesAfter = 0;
    for (const [tech, ids] of Object.entries(board)) {
      if (!ids.length) continue;
      routePlans.push({ tech, order: ids });
      milesAfter += routeMiles(
        ids
          .map((id) => byId.get(id))
          .filter((s): s is OptiStop => !!s)
          .map((s) => ({ lat: s.lat, lng: s.lng })),
      );
    }
    const reassignments: { id: string; from: string; to: string }[] = [];
    for (const s of stops) {
      const cur = assignOf.get(s.id);
      if (cur && cur !== s.empno) reassignments.push({ id: s.id, from: s.empno, to: cur });
    }
    return {
      reassignments,
      routePlans,
      movedCount: reassignments.length,
      savedMiles: Math.max(0, plan.milesBefore - milesAfter),
    };
  }, [board, assignOf, stops, byId, plan.milesBefore]);

  const liveAfter = plan.milesBefore - commit.savedMiles;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="xl"
        icon={<Sparkles className="h-4 w-4 text-brand-500" />}
        title="Optimize whole day"
        subtitle={
          commit.savedMiles > 0.1 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">
              <TrendingDown className="h-3 w-3" /> saves ~{commit.savedMiles.toFixed(0)} mi
            </span>
          ) : (
            "routes already efficient"
          )
        }
        footer={
          <div className="flex items-center justify-between gap-2">
            {/* OPTIMIZE-UX-02: say out loud that applying is reversible —
                "unsure if I can trust the moves" came from not knowing this. */}
            <span className="min-w-0 truncate text-[11px] text-slate-400">
              Nothing's final — Undo appears after applying, and the board still drags like normal.
            </span>
            <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onApply(commit)}
              disabled={applying}
              className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {applying
                ? "Applying…"
                : commit.movedCount > 0
                  ? `Apply ${commit.movedCount} move${commit.movedCount === 1 ? "" : "s"} + reorder`
                  : "Apply optimized order"}
            </button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Headline before → after (live) */}
          <div className="flex items-center justify-center gap-3 rounded-xl border border-hairline bg-slate-50 p-3 text-center">
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Today
              </div>
              <div className="text-lg font-bold tabular-nums text-slate-700">
                {plan.milesBefore.toFixed(0)} mi
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300" />
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-emerald-600">
                Optimized
              </div>
              <div className="text-lg font-bold tabular-nums text-emerald-700">
                {liveAfter.toFixed(0)} mi
              </div>
            </div>
          </div>

          {/* OPTIMIZE-UX-02 (Seth 2026-10-06: "having a hard time visualizing
              the actual changes... unsure if I can trust the moves"): the
              changes come FIRST, in plain English — one row per call that
              changes hands, with what the call is and a one-click keep-where-
              it-was. The lane board below stays for fine-tuning. */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-700">
              <ArrowRight className="h-3.5 w-3.5 text-emerald-600" />
              What changes
              <span className="rounded-full bg-emerald-50 px-1.5 text-[11px] font-bold text-emerald-700">
                {commit.movedCount}
              </span>
              <span className="font-normal text-slate-400">
                {commit.movedCount === 0
                  ? "no calls change hands — just smarter run orders"
                  : `call${commit.movedCount === 1 ? "" : "s"} handed to a different tech — everything else just runs in better order`}
              </span>
            </div>
            {commit.movedCount > 0 && (
              <ul className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-hairline">
                {commit.reassignments.map((m) => {
                  const s = byId.get(m.id);
                  if (!s) return null;
                  return (
                    <li key={m.id} className="flex items-center gap-2.5 bg-white px-3 py-2 text-xs">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate font-semibold text-slate-800">{s.name}</span>
                          {s.window && <span className="shrink-0 rounded bg-slate-100 px-1 text-[10px] font-semibold text-slate-600">{s.window}</span>}
                          {s.city && <span className="shrink-0 text-[11px] text-slate-400">{s.city}</span>}
                          {s.asap && <span className="shrink-0 rounded bg-red-100 px-1 text-[10px] font-bold text-red-700">ASAP</span>}
                        </div>
                        {s.note && <div className="mt-0.5 line-clamp-1 text-[11px] text-slate-500">{s.note}</div>}
                      </div>
                      <span className="flex shrink-0 items-center gap-1 text-[11px]">
                        <span className="text-slate-400">{techName(m.from)}</span>
                        <ArrowRight className="h-3 w-3 text-emerald-600" />
                        <span className="font-semibold text-emerald-700">{techName(m.to)}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setPreview({
                            dispatch: m.id, label: s.name ?? m.id,
                            fromEmpno: m.from, fromName: techName(m.from),
                            toEmpno: m.to, toName: techName(m.to), savingMiles: 0,
                          })
                        }
                        title="See both techs' days side by side with this move"
                        className="shrink-0 rounded-md border border-hairline px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => revertToOrigin(m.id)}
                        title={`Keep this call on ${techName(m.from)}`}
                        className="shrink-0 rounded-md border border-hairline px-2 py-0.5 text-[11px] font-medium text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600"
                      >
                        Keep put
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* The editable board — drag calls within / between lanes */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-700">
              <Route className="h-3.5 w-3.5 text-brand-500" />
              Fine-tune the lanes
              <span className="font-normal text-slate-400">
                drag a call to reorder a tech's day or hand it to someone else · hover any call for its notes
              </span>
            </div>
            <div
              ref={containerRef}
              onPointerMove={onDragMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              className="flex select-none gap-2 overflow-x-auto pb-1"
            >
              {laneTechs.map((tech) => {
                const ids = board[tech] ?? [];
                const laneStops = ids
                  .map((id) => byId.get(id))
                  .filter((s): s is OptiStop => !!s);
                const laneMiles = routeMiles(
                  laneStops.map((s) => ({ lat: s.lat, lng: s.lng })),
                );
                const isDropTarget =
                  dragId != null && assignOf.get(dragId) !== tech;
                return (
                  <div
                    key={tech}
                    data-lane={tech}
                    className={cn(
                      "flex w-56 shrink-0 flex-col rounded-lg border bg-slate-50/60 transition-colors",
                      isDropTarget
                        ? "border-brand-400 ring-2 ring-brand-200"
                        : "border-hairline",
                    )}
                  >
                    <div className="flex items-center justify-between gap-1 border-b border-hairline px-2.5 py-2">
                      <span className="truncate text-sm font-semibold text-slate-800">
                        {techName(tech)}
                      </span>
                      <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                        {laneStops.length} · {laneMiles.toFixed(0)} mi
                      </span>
                    </div>
                    <div className="min-h-[2.5rem] space-y-1.5 p-2">
                      {laneStops.length === 0 ? (
                        <p className="px-1 py-2 text-center text-[10px] text-slate-300">
                          drop a call here
                        </p>
                      ) : (
                        laneStops.map((s, i) => {
                          const movedHere =
                            assignOf.get(s.id) === tech && tech !== s.empno;
                          return (
                            <div
                              key={s.id}
                              data-stop={s.id}
                              onMouseEnter={(e) => showHover(s, e)}
                              onMouseLeave={() => setHover(null)}
                              className={cn(
                                "rounded-md border px-2 py-1.5 text-xs transition-shadow",
                                movedHere
                                  ? "border-emerald-300 bg-emerald-50"
                                  : "border-hairline bg-white",
                                dragId === s.id &&
                                  "pointer-events-none scale-[1.03] opacity-95 shadow-e2 ring-2 ring-brand-500 border-t-[3px] border-t-brand-500",
                              )}
                            >
                              <div className="flex items-center gap-1.5">
                                <span
                                  onPointerDown={(e) => startDrag(e, s.id)}
                                  aria-label="Drag to reorder or reassign"
                                  className="shrink-0 cursor-grab touch-none text-slate-300 transition-colors hover:text-slate-500 active:cursor-grabbing"
                                >
                                  <GripVertical className="h-3.5 w-3.5" />
                                </span>
                                <span className="w-3.5 shrink-0 text-center text-[11px] font-bold tabular-nums text-slate-400">
                                  {i + 1}
                                </span>
                                <span className="min-w-0 flex-1 truncate font-medium text-slate-800">
                                  {s.name}
                                </span>
                                {s.asap && (
                                  <span className="shrink-0 rounded bg-red-100 px-1 text-[10px] font-bold text-red-700">
                                    ASAP
                                  </span>
                                )}
                                {s.lockReason && (
                                  <Lock
                                    className="h-3 w-3 shrink-0 text-slate-300"
                                    aria-label={`${s.lockReason} — stays with this tech`}
                                  />
                                )}
                              </div>
                              <div className="mt-1 flex items-center gap-1.5 pl-9 text-[11px] text-slate-500">
                                {s.window && (
                                  <span className="rounded bg-slate-100 px-1 font-semibold">
                                    {s.window}
                                  </span>
                                )}
                                {s.city && <span className="truncate">{s.city}</span>}
                              </div>
                              {/* OPTIMIZE-UX-02: what the call IS, on the card —
                                  not only behind a hover. */}
                              {s.note && (
                                <div className="mt-0.5 line-clamp-1 pl-9 text-[10px] leading-snug text-slate-400">
                                  {s.note}
                                </div>
                              )}
                              {movedHere && (
                                <div className="mt-1 flex items-center justify-between gap-1 pl-9">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPreview({
                                        dispatch: s.id,
                                        label: s.name ?? s.id,
                                        fromEmpno: s.empno,
                                        fromName: techName(s.empno),
                                        toEmpno: tech,
                                        toName: techName(tech),
                                        savingMiles: 0,
                                      })
                                    }
                                    title="See the before/after for this move"
                                    className="truncate text-[10px] font-medium text-emerald-700 hover:underline"
                                  >
                                    ← from {techName(s.empno)}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => revertToOrigin(s.id)}
                                    title={`Keep on ${techName(s.empno)}`}
                                    className="shrink-0 rounded px-1 text-[10px] font-medium text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                  >
                                    revert
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p
              className={cn(
                "mt-1 text-[11px] transition-colors",
                reject ? "font-medium text-rose-500" : "text-slate-400",
              )}
            >
              {reject
                ? "That call can't go to that tech — it's locked to its tech, or that tech isn't qualified."
                : "Locked calls (lock icon) can be reordered but not handed to another tech."}
            </p>
          </div>

          {/* Locked summary */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-700">
              <Lock className="h-3.5 w-3.5 text-slate-400" />
              Locked to their tech
              <span className="rounded-full bg-slate-100 px-1.5 text-[11px] font-bold text-slate-600">
                {locked.length}
              </span>
            </div>
            {locked.length === 0 ? (
              <p className="text-[11px] text-slate-400">
                No locked calls today — everything was free to optimize.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {lockedByReason.map(([reason, n]) => (
                  <span
                    key={reason}
                    className="inline-flex items-center gap-1 rounded-full border border-hairline bg-white px-2 py-0.5 text-[11px] text-slate-600"
                  >
                    <Lock className="h-2.5 w-2.5 text-slate-400" />
                    {n} {reason}
                  </span>
                ))}
              </div>
            )}
            <p className="mt-1.5 text-[11px] leading-snug text-slate-400">
              {movableCount} call{movableCount === 1 ? "" : "s"} are free to move
              (windowed service, long, quote). Part calls, tech-specific calls,
              2-tech calls, installs, return visits and customer tech-requests stay
              put — they only move when their tech is out.
            </p>
          </div>
        </div>
      </Modal>

      {/* Read-only "where does it fit" side-by-side for a moved call. */}
      {preview && (
        <MovePreview
          move={preview}
          rows={rows}
          recoTechs={recoTechs}
          previewOnly
          onConfirm={() => setPreview(null)}
          onClose={() => setPreview(null)}
        />
      )}

      {/* Hover preview — what the call is about. */}
      {hover &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[70] w-72"
            style={{
              left: Math.min(hover.x, window.innerWidth - 300),
              top: Math.min(hover.y, window.innerHeight - 180),
            }}
          >
            <div className="rounded-lg border border-hairline bg-white/98 p-3 shadow-e3 backdrop-blur">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-slate-900">
                  {hover.stop.name}
                </span>
                {hover.stop.asap && (
                  <span className="rounded bg-red-100 px-1 text-[10px] font-bold text-red-700">
                    ASAP
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                {hover.stop.window && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">
                    {hover.stop.window}
                  </span>
                )}
                {hover.stop.city && <span>{hover.stop.city}</span>}
                {hover.stop.lockReason && (
                  <span className="inline-flex items-center gap-0.5 text-slate-400">
                    <Lock className="h-3 w-3" /> {hover.stop.lockReason}
                  </span>
                )}
              </div>
              {hover.stop.note ? (
                <p className="mt-1.5 line-clamp-4 text-xs leading-snug text-slate-600">
                  {hover.stop.note}
                </p>
              ) : (
                <p className="mt-1.5 text-xs italic text-slate-300">No job notes.</p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
